import { Box, Button, Group, Paper, Select, Stack, Text, Textarea } from "@mantine/core"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import type { IInboundOrder } from "../../apis/inbound-order"
import { DeliveryActionButton } from "../delivery/action-controls"
import { translateBackendError } from "../delivery/translate-error"
import { useDriverOptions, useInboundMutations, useVehicleOptions } from "./hooks"
import { INBOUND_ACTION_PERMISSION, InboundAction, InboundActionSubject, useInboundActions } from "./inbound-actions"

type Props = Readonly<{
  order: IInboundOrder
  onPickupStart: () => void
  onPickupComplete: () => void
  onReceive: () => void
}>

// Order of the buttons in «Дараагийн үйлдэл»; the status decides which of them apply.
const ACTIONS: InboundAction[] = ["approve", "dispatch", "pickupStart", "pickupComplete", "arrive", "receive", "cancel"]

/** «Дараагийн үйлдэл»: the actions that belong to the current status, all visible, gated by IBN_* permissions. */
export function InboundActionsPanel({ order, onPickupStart, onPickupComplete, onReceive }: Props) {
  const { t } = useTranslation(["inbound", "common"])
  const { stateOf } = useInboundActions()
  const mutations = useInboundMutations()
  const [form, setForm] = useState<"dispatch" | "cancel" | null>(null)
  const [driverId, setDriverId] = useState<string | null>(null)
  const [vehicleId, setVehicleId] = useState<string | null>(null)
  const [cancelReason, setCancelReason] = useState("")

  const subject: InboundActionSubject = { status: order.status, pickupStarted: !!order.custodyTransferOutboundId }
  const visible = ACTIONS.filter((a) => stateOf(a, subject).applies)
  const drivers = useDriverOptions(form === "dispatch")
  const vehicles = useVehicleOptions(form === "dispatch")

  if (visible.length === 0) return null

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
      setForm(null)
    } catch {
      // shown through mutations.error
    }
  }

  const handlers: Record<string, () => void> = {
    approve: () => run(() => mutations.approve(order.id)),
    dispatch: () => setForm(form === "dispatch" ? null : "dispatch"),
    pickupStart: onPickupStart,
    pickupComplete: onPickupComplete,
    arrive: () => run(() => mutations.arrive(order.id)),
    receive: onReceive,
    cancel: () => setForm(form === "cancel" ? null : "cancel"),
  }

  const labelKey: Record<string, string> = {
    approve: "detail.approve",
    dispatch: "detail.assignDriver",
    pickupStart: "detail.startPickup",
    pickupComplete: "detail.completePickup",
    arrive: "detail.markArrived",
    receive: "detail.receive",
    cancel: "detail.cancel",
  }

  return (
    <Paper withBorder radius="md" p="md">
      <Text fz={14} fw={700} tt="uppercase" c="gray.8" mb="sm">
        {t("inbound:detail.nextAction")}
      </Text>

      <Group gap="sm" mb="xs">
        {visible.map((action) => (
          <DeliveryActionButton
            key={action}
            state={stateOf(action, subject)}
            variant={action === "cancel" ? "default" : "filled"}
            color={action === "cancel" ? undefined : "dark"}
            loading={mutations.submitting && form === null && action !== "cancel"}
            onClick={handlers[action]}
          >
            {t(`inbound:${labelKey[action]}`)}
          </DeliveryActionButton>
        ))}
      </Group>

      <Text fz={14} c="gray.8">
        {visible.map((a) => `${t(`inbound:${labelKey[a]}`)}: ${INBOUND_ACTION_PERMISSION[a]}`).join(" · ")}
      </Text>

      {form === "dispatch" && (
        <Stack gap="xs" mt="md">
          <Select
            label={t("inbound:detail.driverLabel")}
            placeholder={t("inbound:detail.driverPlaceholder")}
            data={drivers.options}
            value={driverId}
            onChange={setDriverId}
            searchable
            required
            nothingFoundMessage={t("inbound:detail.nothingFound")}
            description={drivers.permissionReason ?? undefined}
            disabled={!!drivers.permissionReason}
          />
          <Select
            label={t("inbound:detail.vehicleLabel")}
            placeholder={t("inbound:detail.vehiclePlaceholder")}
            data={vehicles.options}
            value={vehicleId}
            onChange={setVehicleId}
            searchable
            clearable
            nothingFoundMessage={t("inbound:detail.nothingFound")}
            description={vehicles.permissionReason ?? undefined}
            disabled={!!vehicles.permissionReason}
          />
          <Group gap="xs">
            <Button
              disabled={!driverId}
              loading={mutations.submitting}
              onClick={() => driverId && run(() => mutations.dispatch(order.id, { driverId, vehicleId: vehicleId ?? undefined }))}
            >
              {t("inbound:detail.assign")}
            </Button>
            <Button variant="subtle" onClick={() => setForm(null)}>
              {t("inbound:detail.back")}
            </Button>
          </Group>
        </Stack>
      )}

      {form === "cancel" && (
        <Stack gap="xs" mt="md">
          <Textarea placeholder={t("inbound:detail.cancelReason")} value={cancelReason} onChange={(e) => setCancelReason(e.currentTarget.value)} rows={2} />
          <Group gap="xs">
            <Button disabled={!cancelReason.trim()} loading={mutations.submitting} onClick={() => run(() => mutations.cancel(order.id, cancelReason.trim()))}>
              {t("inbound:detail.cancelBtn")}
            </Button>
            <Button variant="subtle" onClick={() => setForm(null)}>
              {t("inbound:detail.back")}
            </Button>
          </Group>
        </Stack>
      )}

      {mutations.error && (
        <Box mt="xs">
          <Text fz={14} fw={600}>
            {translateBackendError(mutations.error)}
          </Text>
        </Box>
      )}
    </Paper>
  )
}
